import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill, TextArea, Input } from "../../components/common/Primitives";
import { Building2, ShieldCheck, RefreshCcw, AlertTriangle, Search, FileText } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const STATUS_LABELS = {
  not_started: { label: "Not Started", tone: "muted" },
  auto_fetch_in_progress: { label: "Auto-Fetch In Progress", tone: "gold" },
  auto_fetched_confirmation_pending: { label: "Confirmation Pending", tone: "gold" },
  auto_fetched_confirmed: { label: "Auto-Fetched · Confirmed", tone: "success" },
  auto_fetch_failed: { label: "Auto-Fetch Failed", tone: "warn" },
  manual_entry_pending_verification: { label: "Manual · Pending", tone: "warn" },
  admin_review_required: { label: "Review Required", tone: "warn" },
  verified: { label: "Verified", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  more_info_required: { label: "More Info", tone: "warn" },
};

const SEVERITY_TONE = { high: "danger", medium: "warn", low: "muted" };

export function AdminCompanyIntelligenceList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/company-intelligence${filter ? `?status=${filter}` : ""}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [filter]);

  const filtered = q.trim()
    ? items.filter((i) => JSON.stringify(i).toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <DashboardShell title="Company Intelligence" subtitle="ADMIN · MCA VERIFICATION QUEUE" actions={
      <Link to="/admin/mca-lookup" className="px-4 py-2 border border-navy/20 rounded-md text-sm font-heading hover:bg-navy/[0.04]" data-testid="admin-ci-mca-lookup-cta">
        <Search size={14} className="inline mr-1" /> Ad-hoc MCA Lookup
      </Link>
    }>
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3 mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-navy/15 rounded-md px-3 py-1.5 text-sm font-heading" data-testid="admin-ci-status-filter">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search startup / CIN / founder" className="flex-1 border border-navy/15 rounded-md px-3 py-1.5 text-sm" data-testid="admin-ci-search" />
        <div className="text-xs font-mono text-ink-muted">{filtered.length} results</div>
      </div>
      <Card testId="admin-ci-table-card" className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-ci-table">
          <thead className="bg-navy/[0.04] border-b border-navy/10">
            <tr>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Startup</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">CIN</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Status</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Flags</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">POC</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const m = STATUS_LABELS[s.company_verification_status] || STATUS_LABELS.not_started;
              return (
                <tr key={s.startup_id} className="border-b border-navy/[0.06] hover:bg-navy/[0.02]" data-testid={`admin-ci-row-${s.startup_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-heading text-ink">{s.startup_name}</div>
                    <div className="text-xs text-ink-muted">{s.legal_entity_name}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-ink-muted">{s.cin || "—"}</td>
                  <td className="px-4 py-3"><Pill tone={m.tone}>{m.label}</Pill></td>
                  <td className="px-4 py-3">
                    {s.mismatch_count > 0 ? <Pill tone="warn"><AlertTriangle size={10} className="inline mr-1" />{s.mismatch_count}</Pill> : <span className="text-xs text-ink-muted">—</span>}
                    {s.is_manual_entry && <Pill tone="muted">Manual</Pill>}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-muted">{s.assigned_poc_email || "unassigned"}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/company-intelligence/${s.startup_id}`} className="text-[#8A6600] font-heading text-sm hover:underline" data-testid={`admin-ci-open-${s.startup_id}`}>Open →</Link>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-muted">No startups match this filter.</td></tr>}
          </tbody>
        </table>
      </Card>
    </DashboardShell>
  );
}


export function AdminCompanyIntelligenceDetail() {
  const { startupId } = useParams();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/company-intelligence/${startupId}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [startupId]);

  const retry = async () => {
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/admin/company-intelligence/${startupId}/retry`);
      if (resp.outcome === "found") toast.success(`Refetched. ${resp.mismatch_count} mismatch(es).`);
      else toast.warning(`Refetch: ${resp.outcome} — ${resp.message || ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/company-intelligence/${startupId}/verify`, { decision, notes });
      toast.success(`Marked ${decision.replace(/_/g, " ")}.`);
      setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <DashboardShell title="Company Intelligence"><div className="text-ink-muted">Loading…</div></DashboardShell>;

  const s = data.startup;
  const p = data.company_profile || {};
  const statusMeta = STATUS_LABELS[s.company_verification_status] || STATUS_LABELS.not_started;

  return (
    <DashboardShell title={s.startup_name || "Company Intelligence"} subtitle="ADMIN · COMPANY DETAIL" actions={
      <Link to="/admin/company-intelligence" className="text-sm text-[#8A6600] hover:underline">← All companies</Link>
    }>
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card testId="admin-ci-header-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">STARTUP · MCA VERIFICATION</div>
                <div className="font-display text-3xl text-ink mt-1">{p.company_legal_name || s.startup_name}</div>
                <div className="text-xs font-mono text-ink-muted mt-1">CIN: {s.cin || "—"}</div>
              </div>
              <Pill tone={statusMeta.tone} data-testid="admin-ci-status-pill">{statusMeta.label}</Pill>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={retry} disabled={busy || !s.cin} data-testid="admin-ci-retry-btn"><RefreshCcw size={13} className="inline mr-1" /> Retry MCA Fetch</Button>
            </div>
          </Card>

          <Card testId="admin-ci-profile-card">
            <div className="overline mb-3 flex items-center gap-2"><Building2 size={14} /> Company Master Data</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <F label="Legal Name" value={p.company_legal_name} src={p.field_sources?.company_legal_name} />
              <F label="CIN" value={p.cin} src={p.field_sources?.cin} />
              <F label="Status" value={p.company_status} src={p.field_sources?.company_status} />
              <F label="Class" value={p.company_class} src={p.field_sources?.company_class} />
              <F label="Category" value={p.company_category} src={p.field_sources?.company_category} />
              <F label="Incorporated" value={p.date_of_incorporation} src={p.field_sources?.date_of_incorporation} />
              <F label="State" value={p.registered_state} src={p.field_sources?.registered_state} />
              <F label="RoC" value={p.roc_jurisdiction} src={p.field_sources?.roc_jurisdiction} />
              <F label="Auth Capital" value={fmt(p.authorised_capital_inr)} src={p.field_sources?.authorised_capital_inr} />
              <F label="Paid-up Capital" value={fmt(p.paid_up_capital_inr)} src={p.field_sources?.paid_up_capital_inr} />
              <F label="Listing" value={p.listing_status} src={p.field_sources?.listing_status} />
              <F label="Last AGM" value={p.last_agm_date || "—"} src={p.field_sources?.last_agm_date} />
              <F label="Last Balance Sheet" value={p.last_balance_sheet_date || "—"} src={p.field_sources?.last_balance_sheet_date} />
              <div className="col-span-2">
                <div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Registered Office</div>
                <div className="text-sm">{p.registered_office_address || "—"}</div>
              </div>
              {p.directors && p.directors.length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Directors ({p.directors.length})</div>
                  <div className="space-y-1">
                    {p.directors.map((d, i) => (
                      <div key={i} className="text-xs bg-navy/[0.03] rounded px-2 py-1">
                        <b>{d.name}</b> · DIN {d.din} · {d.role}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {p.charges && p.charges.length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Charges ({p.charges.length})</div>
                  <div className="space-y-1">
                    {p.charges.map((c, i) => (
                      <div key={i} className="text-xs bg-rose-500/[0.05] border border-rose-500/20 rounded px-2 py-1">
                        <b>{c.holder}</b> · ₹{Number(c.amount_inr).toLocaleString("en-IN")} · <Pill tone="warn">{c.status}</Pill>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card testId="admin-ci-lookups-card">
            <div className="overline mb-3 flex items-center gap-2"><FileText size={14} /> Lookup History ({data.lookups?.length || 0})</div>
            {(!data.lookups || data.lookups.length === 0) && <div className="text-sm text-ink-muted">No lookups yet.</div>}
            <div className="space-y-1">
              {(data.lookups || []).slice(0, 10).map((l) => (
                <div key={l.lookup_id} className="flex items-center justify-between text-xs border-b border-navy/[0.05] py-1.5">
                  <div className="font-mono text-ink-muted">{l.lookup_id}</div>
                  <div>{l.initiator_email}</div>
                  <Pill tone={l.outcome === "found" ? "success" : l.outcome === "not_found" ? "muted" : "danger"}>{l.outcome}</Pill>
                  <div className="text-ink-muted">{l.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card testId="admin-ci-decision-card">
            <div className="overline mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Verification Decision</div>
            <TextArea label="Internal notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add internal note (optional)" testId="admin-ci-notes-input" />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button variant="gold" onClick={() => decide("verified")} disabled={busy} data-testid="admin-ci-verify-btn">Verify</Button>
              <Button variant="ghost" onClick={() => decide("more_info_required")} disabled={busy} data-testid="admin-ci-more-info-btn">More Info</Button>
              <Button variant="ghost" onClick={() => decide("admin_review_required")} disabled={busy} data-testid="admin-ci-review-btn">Review Later</Button>
              <Button variant="ghost" onClick={() => decide("rejected")} disabled={busy} data-testid="admin-ci-reject-btn"><span className="text-rose-700">Reject</span></Button>
            </div>
          </Card>

          <Card testId="admin-ci-mismatches-card">
            <div className="overline mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Mismatch Flags ({data.mismatches?.length || 0})</div>
            {(!data.mismatches || data.mismatches.length === 0) ? (
              <div className="text-sm text-ink-muted italic">No mismatches detected.</div>
            ) : (
              <div className="space-y-2">
                {data.mismatches.map((m, i) => (
                  <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: m.severity === "high" ? "#EF4444" : m.severity === "medium" ? "#F59E0B" : "#9CA3AF" }} data-testid={`admin-ci-flag-${m.code}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Pill tone={SEVERITY_TONE[m.severity] || "muted"}>{m.severity}</Pill>
                      <span className="text-xs font-mono text-ink-muted">{m.code}</span>
                    </div>
                    <div className="text-xs text-ink">{m.message}</div>
                    {m.expected != null && <div className="text-[10px] font-mono text-ink-muted mt-0.5">expected: {String(m.expected)} · actual: {String(m.actual)}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {s.verification_notes && s.verification_notes.length > 0 && (
            <Card testId="admin-ci-history-card">
              <div className="overline mb-3">Verification History</div>
              <div className="space-y-2 text-xs">
                {s.verification_notes.slice().reverse().slice(0, 8).map((n, i) => (
                  <div key={i} className="border-l border-navy/20 pl-2 py-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Pill tone={STATUS_LABELS[n.decision]?.tone || "muted"}>{STATUS_LABELS[n.decision]?.label || n.decision}</Pill>
                      <span className="font-mono text-ink-muted">{n.at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-ink-muted">by {n.by}</div>
                    {n.note && <div className="text-ink mt-1">{n.note}</div>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}


export function AdminMCALookup() {
  const [cin, setCin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const doLookup = async () => {
    if (!cin.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mca-lookup", { cin: cin.trim().toUpperCase() });
      setResult(data);
      loadLogs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/admin/mca-lookup/logs?limit=20");
      setLogs(data.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadLogs(); }, []);

  return (
    <DashboardShell title="MCA Ad-hoc Lookup" subtitle="ADMIN · COMPANY INTELLIGENCE" actions={
      <Link to="/admin/company-intelligence" className="text-sm text-[#8A6600] hover:underline">← Back</Link>
    }>
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card testId="admin-mca-lookup-card">
            <div className="overline mb-3 flex items-center gap-2"><Search size={14} /> Search MCA Master Data</div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input label="CIN / LLPIN" value={cin} onChange={(e) => setCin(e.target.value.toUpperCase())} placeholder="U40108DL2022PTC410123" testId="admin-mca-cin-input" />
              </div>
              <Button variant="gold" onClick={doLookup} disabled={busy} data-testid="admin-mca-lookup-btn">{busy ? "Fetching…" : "Lookup"}</Button>
            </div>
            {result && (
              <div className="mt-4">
                {result.outcome === "found" ? (
                  <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-md p-4" data-testid="admin-mca-result-found">
                    <Pill tone="success">FOUND</Pill>
                    <div className="font-heading text-lg text-ink mt-2">{result.company.company_legal_name}</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                      <div><span className="text-ink-muted">Status:</span> {result.company.company_status}</div>
                      <div><span className="text-ink-muted">Class:</span> {result.company.company_class}</div>
                      <div><span className="text-ink-muted">Incorporated:</span> {result.company.date_of_incorporation}</div>
                      <div><span className="text-ink-muted">State:</span> {result.company.registered_state}</div>
                      <div><span className="text-ink-muted">Paid-up:</span> {fmt(result.company.paid_up_capital_inr)}</div>
                      <div><span className="text-ink-muted">Directors:</span> {(result.company.directors || []).length}</div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-amber-500/40 bg-amber-500/5 rounded-md p-4" data-testid="admin-mca-result-fail">
                    <Pill tone="warn">{result.outcome?.toUpperCase()}</Pill>
                    <div className="mt-2 text-sm text-ink">{result.message || "No record found."}</div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card testId="admin-mca-logs-card">
            <div className="overline mb-3">Recent Lookups</div>
            {logs.length === 0 && <div className="text-sm text-ink-muted italic">No lookups yet.</div>}
            <div className="space-y-1.5">
              {logs.map((l) => (
                <div key={l.lookup_id} className="text-xs border-b border-navy/[0.05] pb-1.5">
                  <div className="flex items-center gap-2">
                    <Pill tone={l.outcome === "found" ? "success" : l.outcome === "not_found" ? "muted" : "danger"}>{l.outcome}</Pill>
                    <span className="font-mono text-ink-muted">{l.cin}</span>
                  </div>
                  <div className="text-ink-muted mt-0.5">{l.initiator_email} · {l.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </DashboardShell>
  );
}


function fmt(v) { return v == null ? "—" : "₹" + Number(v).toLocaleString("en-IN"); }

function F({ label, value, src }) {
  const source = src?.source;
  const tone = source === "api_fetched" ? "success" : source === "admin_verified" ? "success" : source === "founder_declared" ? "warn" : "muted";
  const srcLabel = { api_fetched: "API", admin_verified: "ADMIN", founder_declared: "FOUNDER", unverified: "?" }[source] || "?";
  return (
    <div className="border-b border-navy/8 pb-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</div>
        {source && <Pill tone={tone}>{srcLabel}</Pill>}
      </div>
      <div className="text-sm text-ink mt-1">{value ?? "—"}</div>
    </div>
  );
}
